import type { FC, Key } from 'react';
import { msg } from '@gonfalon/messages/msg';
import { Box } from '@gonfalon/observability-ui/components/Box';
import {
  IconSolidBell,
  IconSolidChartSquareBar,
  IconSolidExclamationCircle,
  IconSolidExternalLink,
  IconSolidFlag,
  IconSolidGithub,
  IconSolidJira,
  IconSolidLogs,
  IconSolidMetrics,
  IconSolidPlayCircle,
  IconSolidSearch,
  IconSolidSlack,
  IconSolidTraces,
} from '@gonfalon/observability-ui/components/icons';
import { Tag, TagGroup, TagList } from '@launchpad-ui/components';

import type { ResourceOrQuery } from '../../../../resources';
import { inferContextType } from '../../../../resources';
import { getContextLabel } from '../../../mentions';

export interface ContextPillsProps {
  context: ResourceOrQuery[];
  onRemove?: (index: number) => void;
}

export const ContextPills: FC<ContextPillsProps> = ({ context, onRemove }) => {
  if (!context || context.length === 0) {
    return null;
  }

  // Convert index-based removal to key-based removal for TagGroup
  const handleRemove = onRemove
    ? (keys: Set<Key>) => {
        // Convert keys back to indices and call onRemove for each
        const indices = Array.from(keys).map((key) => parseInt(String(key), 10));
        // Sort in descending order to remove from end first (prevents index shifting issues)
        indices.sort((a, b) => b - a);
        indices.forEach((index) => onRemove(index));
      }
    : undefined;

  return (
    <Box display="flex" flexWrap="wrap" gap="4">
      <TagGroup aria-label={msg('vega.conversation.contextLabel')} onRemove={handleRemove}>
        <TagList>
          {context.map((resource, index) => (
            <Tag size="small" key={index} id={String(index)}>
              {getIcon(resource)}
              {getContextLabel(resource)}
            </Tag>
          ))}
        </TagList>
      </TagGroup>
    </Box>
  );
};

const getIcon = (resource: ResourceOrQuery) => {
  const type = inferContextType(resource);

  switch (type) {
    case 'Sessions':
      return <IconSolidPlayCircle />;
    case 'Logs':
      return <IconSolidLogs />;
    case 'Metrics':
      return <IconSolidMetrics />;
    case 'Traces':
      return <IconSolidTraces />;
    case 'Errors':
      return <IconSolidExclamationCircle />;
    case 'Dashboard':
      return <IconSolidChartSquareBar />;
    case 'Alert':
      return <IconSolidBell />;
    case 'SavedSearch':
      return <IconSolidSearch />;
    case 'Flag':
      return <IconSolidFlag />;
    case 'ExternalSource': {
      const sourceType = typeof resource.payload?.sourceType === 'string' ? resource.payload.sourceType : undefined;
      switch (sourceType) {
        case 'slack':
          return <IconSolidSlack />;
        case 'jira':
          return <IconSolidJira />;
        case 'github':
          return <IconSolidGithub />;
        default:
          return <IconSolidExternalLink />;
      }
    }
    default:
      return <IconSolidLogs />;
  }
};
